import moment from 'moment-timezone';
import { Admin, TableSchoolFile } from 'shared/types';

export type DateInput = Date | string | number | moment.Moment;

// Maps the stored numeric timezone offset to a DST-aware IANA zone name.
// DB timestamps are UTC; the admin's offset preference (-5/-6/-7/-8) selects the
// zone so display respects daylight saving (e.g. -6 => Central, CST in winter / CDT in summer).
export function offsetToTimeZone(offset?: number): string {
  switch (offset) {
    case -5: return 'America/New_York';
    case -6: return 'America/Chicago';
    case -7: return 'America/Denver';
    case -8: return 'America/Los_Angeles';
    default: return 'UTC';
  }
}

// Localizes a UTC instant (ISO string, epoch ms, or Date) into the admin's
// configured timezone for display.
export const parseISODateFormat = (
  date: DateInput | null | undefined,
  formatTime = 'MM/DD/YYYY h:mmA'
): string => {
  if (date === undefined || date === null || date === '') return '';

  const zone = offsetToTimeZone(getUserTimezoneOffset());
  return moment.utc(date).tz(zone).format(formatTime);
};

export const secondsTransform = (seconds: number): string => {
  const d = Math.floor(seconds / (3600 * 24));
  const h = Math.floor((seconds % (3600 * 24)) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);

  const dDisplay = d > 0 ? d + (d === 1 ? ' day ' : ' days ') : '';
  const hDisplay = h > 0 ? (h < 10 ? `0${h}` : h) : '00';
  const mDisplay = m > 0 ? (m < 10 ? `0${m}` : m) : '00';
  const sDisplay = s > 0 ? (s < 10 ? `0${s}` : s) : '00';

  return `${dDisplay} ${hDisplay}:${mDisplay}:${sDisplay}`;
};

export function formatTime(seconds: number) {
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return '0:00';
  }

  const totalSeconds = Math.floor(seconds);
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;

  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function formatTimeMS(milliseconds: number) {
  if (!Number.isFinite(milliseconds) || milliseconds <= 0) {
    return '0:00';
  }

  const totalSeconds = Math.floor(milliseconds / 1000);
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;

  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export const modifyFiles = (files: TableSchoolFile[], currentUser: Admin | null) => {
  const modifiedSchoolFiles = files.map((item): TableSchoolFile & { key: string } => {
    const { Key, LastModified } = item;
    return {
      key: Key,
      Key,
      LastModified: moment
        .utc(LastModified)
        .utcOffset(currentUser?.timezone ?? 0)
        .format(`[uploaded] M/d/YY [at] H:mma [GMT${currentUser?.timezone ?? 0}]`)
    };
  });
  return modifiedSchoolFiles;
};
export const DATE_FORMAT = 'MM/DD/YYYY h:mmA';

export function getUserTimezoneOffset(): number {
  try {
    const raw = localStorage.getItem('authState');
    if (!raw) return 0;
  
    const parsed = JSON.parse(raw);
    const timezone = parsed?.user?.timezone;
    return timezone ?? 0;
  } catch (e) {
    console.error(e);
    return 0;
  }
}

function normalizeMoment(input: DateInput): moment.Moment {
  const m = moment.isMoment(input) ? input.clone() : moment(input);

  if (!m.isValid()) {
    throw new Error(`Invalid date input: ${input}`);
  }

  return m;
}

export function toUserTimezone(
  input: DateInput,
): moment.Moment {
  const offsetHours = getUserTimezoneOffset();

  const offsetMinutes = offsetHours * 60;

  const m = normalizeMoment(input);

  return m.utc().utcOffset(offsetMinutes, true);
}

export function toUTC(input: DateInput): moment.Moment {
  return normalizeMoment(input).utc();
}

export function toUserTimezoneDate(
  input: DateInput,
): Date {
  return toUserTimezone(input).toDate();
}

export function toUTCDate(input: DateInput): Date {
  return toUTC(input).toDate();
}
